import { DragDrop } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { ApplicationRef, Component, ComponentFactoryResolver, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { fromEvent, of, Subject } from 'rxjs'
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators'
import { BADGE_SIDES, DROP_AREAS, TOOLBAR_ACTIONS } from '../../../enums/badge.enum'
import { BadgeElement, BadgeElementFieldType } from '../../../models/badge-variable-elements.model'
import { BadgeEditorService } from '../../../services/badge-editor.service'
import { TemplateLayout } from '../../../types/badge.type'
import { BadgeElementTooltipSettingsComponent } from '../badge-element-tooltip-settings/badge-element-tooltip-settings.component'
import { BadgeTooltipSettingsComponent } from '../badge-tooltip-settings/badge-tooltip-settings.component'
const BADGE_LEFT_AND_RIGHT_SPACE = 6
const DROP_AREA_SIZE_IN_MM = 1
const DEFAULT_HEIGHT_IN_MM = 6
const MIN_CONTENT_WIDTH_PERCENT = 50

@Component({
  selector: 'app-badge-template-design-area',
  templateUrl: './badge-template-design-area.component.html',
  styleUrls: ['./badge-template-design-area.component.scss'],
  standalone: true,
  imports: [CommonModule, BadgeTooltipSettingsComponent]
})
export class BadgeTemplateDesignAreaComponent implements OnInit, OnDestroy {
  @Input() public selectedTemplate!: TemplateLayout | null
  @Input() public badgeForm!: FormGroup

  public aspectRatio!: number
  public fields: BadgeElement[][] = []
  public isDragging: boolean = false
  public variableElementFieldType = BadgeElementFieldType
  public currentSide!: BADGE_SIDES
  public draggedItemId!: number
  public isDropValid: boolean = true
  public pushedSpace: number = 0
  public badgeSides = BADGE_SIDES
  private _draggedVariableElement?: BadgeElement
  private _minColumnWidthInMM: number = 40
  private _customizedHeightInMM: number = DEFAULT_HEIGHT_IN_MM
  private _destroy$ = new Subject<void>()
  private _columnsSubscriptions$ = new Subject<void>()
  public frontBadgeBackground?: string
  public backBadgeBackground?: string
  private readonly _dropAreasList: string[] = [DROP_AREAS.TOP, DROP_AREAS.BOTTOM, DROP_AREAS.LEFT, DROP_AREAS.RIGHT, DROP_AREAS.COL_TOP, DROP_AREAS.COL_BOTTOM]
  @ViewChild('badgeContainer') badgeContainer!: ElementRef<HTMLDivElement>
  @ViewChild('badgeDesignContainer') badgeDesignContainer!: ElementRef<HTMLDivElement>

  constructor(
    private _BadgeService: BadgeEditorService,
    private _DragDrop: DragDrop,
    private _ComponentFactoryResolver: ComponentFactoryResolver,
    private _Injector: Injector,
    private _ApplicationRef: ApplicationRef
  ) {}

  ngOnInit(): void {
    this.frontBadgeBackground = this._BadgeService.frontFixedElement
    this.backBadgeBackground = this._BadgeService.backFixedElement
    this._addSubscriptions()
    this.aspectRatio = this.selectedTemplate!.width / this.selectedTemplate!.height
  }

  /**
   * @method _updateBackgroundImage
   * @description update the background image inside the badge
   * @returns void
   */
  private _updateBackgroundImage(): void {
    const badge = document.getElementById('badge')
    if (!badge) return
    badge.style.backgroundImage = this[`${this.currentSide}BadgeBackground`] ? `url("${this[`${this.currentSide}BadgeBackground`]}")` : ''
    this._updateBadgeForm()
  }

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
    this._columnsSubscriptions$.next()
    this._columnsSubscriptions$.complete()
  }

  /**
   * @method _addSubscriptions
   * @description Add subscriptions to the badge service to listen for dragging and dropping events
   * @returns void
   */
  private _addSubscriptions(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(150),
        switchMap((event) => of(event)),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this._createBadgeContent()
      })

    this._BadgeService.toolbarAction$.pipe(takeUntil(this._destroy$)).subscribe(({ action, payload }) => {
      switch (action) {
        case TOOLBAR_ACTIONS.SWITCH_SIDE:
          this._handleSwitchSide(payload as BADGE_SIDES)
          break
        case TOOLBAR_ACTIONS.PUSH_UP:
          this._pushBadgeUp(payload as number)
          break
        case TOOLBAR_ACTIONS.PUSH_DOWN:
          this._pushBadgeDown(payload as number)
          break
        case TOOLBAR_ACTIONS.CONTENT_WIDTH_INCREASE:
        case TOOLBAR_ACTIONS.CONTENT_WIDTH_DECREASE:
          this._setBadgeContentWidth(payload as number)
          break
      }
    })

    this._BadgeService.isDraggingStarted$.pipe(takeUntil(this._destroy$)).subscribe((variableElement) => {
      // Add empty badge content container if it doesn't exist to validate the drop
      if (!document?.querySelector('#badge-content-container')) this._createBadgeContentContainer(variableElement as BadgeElement)
      else if (this.fields.length) {
        this._handleDroppingIfBadgeHasSpaceToDrop(variableElement as BadgeElement)
      }
    })
    this._BadgeService.isDragging$.pipe(takeUntil(this._destroy$)).subscribe((isDragging) => {
      this.isDragging = isDragging
      if (isDragging && !this.fields.length && this.currentSide === BADGE_SIDES.FRONT_SIDE) this._createDropZone()
      else this._removeElement('drop-zone')
    })
    this._BadgeService.hasDropped$.pipe(takeUntil(this._destroy$)).subscribe((element) => {
      if (this.currentSide === BADGE_SIDES.BACK_SIDE) return
      const badge = document.querySelector('#badge') as HTMLDivElement
      if (element?.targetElement && (Object.values(element.targetElement.classList).some((className: string) => this._dropAreasList.includes(className)) || !this.fields.length)) {
        // Destroy column Subscriptions and reset it
        this._columnsSubscriptions$.next()
        this._columnsSubscriptions$.complete()
        this._columnsSubscriptions$ = new Subject<void>()
        this._resetDropZonesValidation()
        this._locateTheElement(element)
        this._createBadgeContent(element)
        this._updateBadgeForm()
      } else if (element.targetElement?.closest('#badge') && badge?.classList?.contains('invalid-dropping')) {
        this.isDropValid = false
        setTimeout(() => {
          if (!this.isDropValid) this.isDropValid = true
        }, 2000)
      }
      badge.classList.remove('invalid-dropping')
    })

    this._BadgeService.badgeBackgroundUploadChanges$.pipe(takeUntil(this._destroy$)).subscribe((response) => {
      // Update FixedElements
      this._BadgeService[`${this.currentSide}FixedElement`] = response?.[`${this.currentSide}_fixed_element`]
      this[`${this.currentSide}BadgeBackground`] = response?.[`${this.currentSide}_fixed_element`]
      this._updateBackgroundImage()
    })
  }

  /**
   * @method _pushBadgeUp
   * @description Push the badge content up based on the space in mm
   * @param space number
   * @returns
   */
  private _pushBadgeUp(space: number): void {
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badgeContentContainer) return
    badgeContentContainer.style.marginBottom = `${space}%`
    this._updateBadgeForm()
  }

  /**
   * @method _pushBadgeDown
   * @description Push the badge content down based on the space in mm
   * @param space number
   * @returns
   */
  private _pushBadgeDown(space: number): void {
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badgeContentContainer) return
    badgeContentContainer.style.marginBottom = `${space}%`
    this._updateBadgeForm()
  }

  /**
   * @method _handleSwitchSide
   * @description Update the badge content based on the side
   * @param badgeSide BADGE_SIDES
   * @returns void
   */
  private _handleSwitchSide(badgeSide: BADGE_SIDES): void {
    this.currentSide = badgeSide
    this.fields = []
    this._removeElement('badge')
    if (!this.badgeForm.get('badge_front_html_content')?.value && this.currentSide === BADGE_SIDES.FRONT_SIDE) {
      this._createEmptyBadge()
      return
    }
    this._initializeBadge()
  }

  /**
   * @method _removeElement
   * @description Remove an element from the badge container based on the id if it exists
   * @param id string
   * @returns void
   */
  private _removeElement(id: string): void {
    const element = this.badgeContainer?.nativeElement.querySelector(`#${id}`)
    if (element) element.remove()
  }

  /**
   * @method _initializeBadge
   * @description Parse the text into HTML and extract the fields from the badge
   * @param htmlString string
   * @returns void
   */
  private _initializeBadge(html?: string): void {
    const htmlString = html || this.badgeForm.get(`badge_${this.currentSide}_html_content`)?.value
    const parser = new DOMParser()
    const badgeDocument = parser.parseFromString(htmlString, 'text/html')
    const newBadge = badgeDocument.getElementById('badge') as HTMLElement
    const newBadgeContentContainer = badgeDocument.getElementById('badge-content-container') as HTMLElement
    // Update persona name fields to be uppercase
    const personaNameFields = badgeDocument.querySelectorAll('[data-element="persona_name"]')
    if (personaNameFields?.length) {
      personaNameFields.forEach((field: HTMLElement) => {
        field.innerHTML = `&nbsp;${field.innerHTML.replace(/&nbsp;/g, '').toUpperCase()}&nbsp;`
      })
    }
    this._extractFieldsFromBadge(newBadge as HTMLDivElement)
    if (newBadgeContentContainer) newBadgeContentContainer.innerHTML = ''
    newBadge.style.height = '100%'
    newBadge.style.width = '100%'
    this.badgeContainer.nativeElement.appendChild(newBadge)
    this._handleBadgeContainerSize()
    this._createBadgeContent()
  }

  /**
   * @method _extractFieldsFromBadge
   * @description Extract the fields from the badge content and save them in the fields array for future rendering
   * @param badge HTMLDivElement
   * @returns void
   */
  private _extractFieldsFromBadge(badge: HTMLDivElement): void {
    const badgeContentContainer = badge.querySelector('#badge-content-container')
    if (!badgeContentContainer) return
    const rows = badgeContentContainer.children
    this.fields = Object.values(rows).map((row) => {
      const columns = row.querySelectorAll('.draggable-column')
      return Object.values(columns).map((column) => {
        const element = column.children[0] as HTMLElement
        const fieldType = element.getAttribute('data-element') as keyof typeof BadgeElementFieldType
        const value =
          fieldType === BadgeElementFieldType.QR_CODE.toLowerCase() || fieldType === BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()
            ? element.querySelector('img')?.src
            : element.innerHTML
        return {
          field_type: fieldType,
          value,
          personaFieldId: element.getAttribute('data-elementid'),
          id: column.getAttribute('key'),
          columnContainerIndex: column.parentElement?.id.split('-')[2] ? +column.parentElement?.id.split('-')[2] - 1 : undefined,
          style: {
            'border-radius': element.style.borderRadius,
            'font-weight': element.style.fontWeight,
            height: (column as HTMLElement).style.height,
            'justify-content': (column as HTMLElement).style.justifyContent,
            'qr-number-display': (element.querySelector('.qr-number') as HTMLElement)?.parentElement?.style?.display
          }
        } as BadgeElement
      })
    })
  }

  /**
   * @method _createEmptyBadge
   * @description Create an empty badge container
   * @param id string
   * @returns void
   */
  private _createEmptyBadge(): void {
    const badge = document.createElement('div')
    badge.id = 'badge'
    badge.style.backgroundColor = '#fff'
    badge.style.width = '100%'
    badge.style.height = '100%'
    badge.style.display = 'flex'
    badge.style.flexDirection = 'column'
    badge.style.alignItems = 'center'
    badge.style.justifyContent = 'center'
    badge.style.userSelect = 'none'
    badge.style.backgroundSize = 'cover'
    badge.style.backgroundRepeat = 'no-repeat'
    badge.setAttribute('data-width', this.selectedTemplate!.width.toString())
    badge.setAttribute('data-height', this.selectedTemplate!.height.toString())
    badge.setAttribute('data-badgeType', this.selectedTemplate!.type.toString())
    this.badgeContainer.nativeElement.appendChild(badge)
    this._updateBackgroundImage()
  }

  /**
   * @method _createDropZone
   * @description Create a drop zone for the badge container in case there are no elements in the badge
   * @returns void
   */
  private _createDropZone(): void {
    if (this.badgeContainer.nativeElement.querySelector('#drop-zone')) return

    const style = {
      'background-image':
        'linear-gradient(to right, #1f6fe0 0 50%, transparent 50% 100%), linear-gradient(to right, #1f6fe0 0 50%, transparent 50% 100%), linear-gradient(to bottom, #1f6fe0 0 50%, transparent 50% 100%), linear-gradient(to bottom, #1f6fe0 0 50%, transparent 50% 100%)',
      'background-position': '0 0, 0 calc(100% - 1px), 0 0, calc(100% - 1px) 0',
      'background-size': '15px 2px, 15px 2px, 2px 15px, 2px 15px',
      'background-repeat': 'repeat no-repeat, repeat no-repeat, no-repeat repeat, no-repeat repeat',
      width: '100%',
      'background-color': '#E5E7EB',
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      'font-weight': '700',
      'font-size': '1.125rem',
      'line-height': '1.75rem',
      color: '#374151',
      height: '30px'
    }

    const styleString = Object.entries(style)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ')

    const badge = document.getElementById('badge') as HTMLDivElement
    badge.innerHTML += `<div id="drop-zone" style="${styleString}">Drop here</div>`
  }

  /**
   * @method _createBadgeContentContainer
   * @description Create a container for the badge content to be displayed in
   * @param badge HTMLDivElement
   * @returns void
   */
  private _createBadgeContentContainer(variableElement: BadgeElement, oldBadgeContent?: HTMLDivElement): void {
    const badge = document.getElementById('badge') as HTMLDivElement
    const badgeContentMargin = oldBadgeContent?.style?.marginBottom
    const badgeContentWidth = oldBadgeContent?.style?.width
    let style = {
      display: 'flex',
      'flex-direction': 'column',
      'align-items': 'center',
      'justify-content': 'center',
      width: badgeContentWidth || '100%',
      'margin-left': 'auto',
      'margin-right': 'auto',
      // Top/bottom dotted guide line (existing behavior). Left/right content bounds are rendered via ::before/::after in SCSS.
      'background-position': '0 0, 0 100%',
      'background-size': '15px 2px',
      'background-repeat': 'repeat no-repeat',
      gap: `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`,
      'padding-left': `${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE / 2)}px`,
      'padding-right': `${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE / 2)}px`,
      'margin-top': '0',
      'margin-bottom': variableElement && oldBadgeContent ? this._calculateBadgeMargin(variableElement, oldBadgeContent as HTMLDivElement) : badgeContentMargin || '0%'
    }
    this._removeElement('badge-content-container')
    const styleString = Object.entries(style)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ')

    badge.innerHTML = `<div id="badge-content-container" style="${styleString}"></div>`
  }

  /**
   * @method _setBadgeContentWidth
   * @description Set badge content container width as a percentage of the full badge width and rebuild content so columns resize.
   * @param widthPercentage number (0-100)
   */
  private _setBadgeContentWidth(widthPercentage: number): void {
    if (!this.selectedTemplate) return
    const badge = document.getElementById('badge') as HTMLDivElement
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLDivElement
    if (!badge || !badgeContentContainer) return

    // Clamp by a safe minimum so we don't violate the minimum element width constraints.
    const minAllowedMM = this._getMinAllowedContentWidthMM()
    const minAllowedPercentage = (minAllowedMM / this.selectedTemplate.width) * 100
    const clamped = Math.max(minAllowedPercentage, Math.min(100, widthPercentage))

    badgeContentContainer.style.width = `${clamped}%`
    badgeContentContainer.style.marginLeft = 'auto'
    badgeContentContainer.style.marginRight = 'auto'

    // Rebuild to recalculate columns widths & drop-areas based on the new content container width.
    this._createBadgeContent()
    this._updateBadgeForm()
  }

  /**
   * @description Compute a safe minimum content width in mm based on current columns count and minimum column width.
   */
  private _getMinAllowedContentWidthMM(): number {
    // New rule: minimum content width is a percentage of the template width (default 50%).
    const minWidth = (this.selectedTemplate!.width * MIN_CONTENT_WIDTH_PERCENT) / 100
    return Math.max(0, Math.min(this.selectedTemplate!.width, Math.ceil(minWidth)))
  }

  /**
   * @description Get the rendered width of the badge content container in pixels.
   */
  private _getBadgeContentContainerWidthPx(): number {
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLDivElement
    if (badgeContentContainer) return badgeContentContainer.getBoundingClientRect().width
    return this.badgeContainer?.nativeElement?.clientWidth || 0
  }

  /**
   * @method _createBadgeContent
   * @description Create the content of the badge based on the fields and inject them into the badge content container
   * @returns void
   */
  private _createBadgeContent(variableElement?: BadgeElement): void {
    this._resetOrder()
    setTimeout(() => {
      const oldBadgeContentContainer = document.querySelector('#badge-content-container') as HTMLDivElement
      this._removeElement('badge-content-container')
      this._handleBadgeContainerSize()
      this._BadgeService.initializeBadgeDimensions()
      this._createBadgeContentContainer(variableElement as BadgeElement, oldBadgeContentContainer)
      const badgeContentContainer = this.badgeContainer.nativeElement.querySelector('#badge-content-container') as HTMLDivElement
      this.fields.forEach((r, i) => this._injectRow(r, i, badgeContentContainer as HTMLDivElement))
      this._initializeCreatedBadge(badgeContentContainer, oldBadgeContentContainer, variableElement)
    })
    this._updateBackgroundImage()
  }

  /**
   * @method _initializeCreatedBadge
   * @description Prepare badge after creation
   * @param badgeContentContainer accepts HTMLElement
   * @param oldBadgeContentContainer accepts HTMLElement
   * @param variableElement accepts BadgeElement or undefined
   * @returns void
   */
  private _initializeCreatedBadge(badgeContentContainer: HTMLElement, oldBadgeContentContainer: HTMLElement, variableElement?: BadgeElement): void {
    // Handle Drop Areas after appending all the rows to the HTML
    this.fields.forEach((r, i) => {
      this._BadgeService.resizeTextValueToFitParent(i)
      this._appendDropAreasOnRow(r, i, `row-${i + 1}`)
    })
    this._handleExtraDropAreas()

    // Handle content container Margin if there is no changes in rows number
    if (oldBadgeContentContainer && (oldBadgeContentContainer?.children.length === badgeContentContainer?.children.length || !variableElement)) {
      badgeContentContainer.style.marginBottom = oldBadgeContentContainer.style.marginBottom
    }
    // Handle badge content container overflow,
    const marginBottom = window.getComputedStyle(badgeContentContainer).marginBottom
    const badgeContentContainerHeight = this._BadgeService.getElementHeight('#badge-content-container') + Math.abs(parseInt(marginBottom))
    if (badgeContentContainerHeight > this._BadgeService.badgeHeightInPX) {
      const space = badgeContentContainerHeight - this._BadgeService.badgeHeightInPX + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM * 2)
      const percentage = (space / badgeContentContainer.getBoundingClientRect().width) * 100
      const elementMargin = parseFloat(oldBadgeContentContainer.style.marginBottom)
      const margin = elementMargin > 0 ? elementMargin - percentage : elementMargin + percentage
      badgeContentContainer.style.marginBottom = `${margin}%`
    }
    this._handleDropAreaSize()
    // Initialize columns elements
    const columns = document.querySelectorAll('.draggable-column')
    Object.values(columns).forEach((column) => {
      this._handleColumnHeightElement(column as HTMLElement)
    })
    this._updateBackgroundImage()
  }

  /**
   * @method _injectRow
   * @description Inject a row of elements into the badge content container
   * @param variableElements BadgeElement[]
   * @param rowIndex number
   * @param parentElement HTMLDivElement
   * @returns void
   */
  private _injectRow(variableElements: BadgeElement[], rowIndex: number, parentElement: HTMLDivElement): void {
    const row = document.createElement('div')
    row.style.display = 'flex'
    row.style.alignItems = 'center'
    row.style.width = '100%'
    row.style.position = 'relative'
    row.style.gap = `${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE / 2)}px`
    row.id = `row-${rowIndex + 1}`
    variableElements.forEach((c, j) => this._injectColumn(c, rowIndex, j, row))
    parentElement.appendChild(row)
  }

  /**
   * @method _injectColumn
   * @description Inject a column of elements into a row
   * @param variableElement BadgeElement
   * @param rowIndex number
   * @param columnIndex number
   * @param parentElement HTMLDivElement
   * @returns void
   */
  private _injectColumn(variableElement: BadgeElement, rowIndex: number, columnIndex: number, parentElement: HTMLDivElement): void {
    //Add fixed column width
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLElement
    const paddingLeftAndRight = parseFloat(badgeContentContainer.style.paddingLeft) * 2
    const sameRowFieldsCount = this._BadgeService.getColumnsCountInRow(rowIndex, this.fields) || 1
    const rowGapsWidth = parseFloat(parentElement.style.gap) * (sameRowFieldsCount - 1)
    const contentWidthPx = this._getBadgeContentContainerWidthPx()
    const columnWidth = (contentWidthPx - rowGapsWidth - paddingLeftAndRight) / sameRowFieldsCount
    const col = document.createElement('div')
    col.style.position = 'relative'
    col.style.display = 'flex'
    col.style.alignItems = 'center'
    col.style.justifyContent = variableElement.style?.['justify-content'] || 'center'
    col.style.width = `${columnWidth}px`
    col.style.boxSizing = 'border-box'
    col.style.height = `${this._calculateDefaultHeightInPX(variableElement)}px` || '0px'
    col.id = `col-${rowIndex + 1}-${columnIndex + 1}`
    col.classList.add('draggable-column')
    col.draggable = true
    col.setAttribute('key', variableElement.id || '')
    col.setAttribute('data-height', variableElement.style?.height || '')
    this._injectField(variableElement, col)
    this._appendColumnDragArea(col)
    this._handleColumnHeightElement(col)
    this._appendTooltipSettings(col, variableElement)
    let columnContainer = parentElement.querySelector(`#container-${rowIndex + 1}-${(variableElement.columnContainerIndex as number) + 1}`) as HTMLElement
    if (!columnContainer) {
      columnContainer = document.createElement('div')
      columnContainer.classList.add('columns-container', 'relative')
      columnContainer.id = `container-${rowIndex + 1}-${(variableElement.columnContainerIndex as number) + 1}`
      columnContainer.style.width = '100%'
      columnContainer.style.width = '100%'
      columnContainer.style.height = '100%'
      columnContainer.style.display = 'flex'
      columnContainer.style.gap = `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
      columnContainer.style.justifyContent = 'center'
      columnContainer.style.alignItems = 'center'
      columnContainer.style.flexDirection = 'column'
    }
    columnContainer.appendChild(col)
    parentElement.appendChild(columnContainer)
  }

  /**
   * @method _injectField
   * @description Inject a field into a column based on the field type
   * @param variableElement BadgeElement
   * @param parentElement HTMLDivElement
   * @returns void
   */
  private _injectField(variableElement: BadgeElement, parentElement: HTMLDivElement): void {
    const rowIndex = +parentElement.id.split('-')[1] - 1
    const maxWidth =
      this._getBadgeContentContainerWidthPx() / this._BadgeService.getColumnsCountInRow(rowIndex, this.fields) - this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE)
    // In case of QR code
    if (variableElement.field_type === BadgeElementFieldType.QR_CODE.toLowerCase() || variableElement.field_type === BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()) {
      const isDTCM = variableElement.field_type === BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()
      // Only Micetribe QR supports hiding/showing the text; DTCM QR text stays visible.
      const isQrNumberHidden = !isDTCM && variableElement.style?.['qr-number-display'] === 'none'
      const div = document.createElement('div')
      div.style.height = '100%'
      div.style.maxWidth = `${maxWidth}px`
      div.style.aspectRatio = isDTCM ? '1 / 0.3' : '1 / 1.2'
      div.style.display = 'flex'
      div.style.flexDirection = 'column'
      div.style.justifyContent = isQrNumberHidden ? 'flex-start' : 'center'
      div.style.alignItems = 'center'
      div.setAttribute('data-element', `${variableElement.field_type}`)
      // Add persona field ID to the div element to be able to select it with ID
      if (variableElement.personaFieldId) div.setAttribute('data-elementid', `${variableElement.personaFieldId}`)
      const img = document.createElement('img')
      // Set QR Code size to be 20 mm with 1/1 aspectRatio as default size
      img.style.height = '78%'
      img.style.width = '100%'
      img.style.aspectRatio = '1/1'
      img.style.objectFit = 'contain'
      img.src = variableElement.value
      const textContainer = document.createElement('div')
      textContainer.style.height = '22%'
      textContainer.style.display = 'flex'
      textContainer.style.width = '100%'
      textContainer.style.alignItems = 'center'
      textContainer.style.justifyContent = 'center'
      textContainer.style.display = isQrNumberHidden ? 'none' : 'flex'
      const p = document.createElement('p')
      p.classList.add('qr-number')
      p.style.color = 'black'
      p.style.textAlign = 'center'
      p.style.lineHeight = 'normal'
      p.style.width = 'fit-content'
      p.style.margin = '0px'
      p.style.whiteSpace = 'nowrap'
      p.innerHTML = isDTCM ? 'b1234d0f-86c5-42c3-b0d2-5cee413f1234' : '1X2Y3Z4'
      textContainer.appendChild(p)
      div.appendChild(img)
      div.appendChild(textContainer)
      parentElement.appendChild(div)
      return
    }

    // In case of Upload Image type
    if (variableElement.field_type === BadgeElementFieldType.UPLOAD_PHOTO.toLowerCase()) {
      const img = document.createElement('img')
      img.src = 'assets/images/upload-photo.svg'
      img.style.maxWidth = `${maxWidth}px`
      img.style.aspectRatio = '1/1'
      img.style.height = '100%'
      img.style.objectFit = 'cover'
      img.style.backgroundColor = '#F9FAFB'
      img.style.borderRadius = variableElement.style?.['border-radius'] || '0%'
      img.classList.add('upload-photo-icon')
      img.setAttribute('data-element', `${variableElement.field_type}`)
      // Add persona field ID to the div element to be able to select it with ID
      if (variableElement.personaFieldId) img.setAttribute('data-elementid', `${variableElement.personaFieldId}`)
      parentElement.appendChild(img)
      return
    }

    // In case of text
    const p = document.createElement('p')
    p.style.fontWeight = variableElement.style?.['font-weight'] || 'normal'
    p.style.fontSize = '1.5rem'
    p.style.color = '#212121'
    p.style.lineHeight = 'normal'
    p.style.margin = '0px'
    p.style.display = 'flex'
    p.style.justifyContent = 'center'
    p.style.alignItems = 'center'
    p.style.whiteSpace = 'nowrap'
    const value = (variableElement.value as any)?.toString()?.replaceAll(/\n/g, '')
    p.innerHTML = variableElement.value?.toString()?.includes('&nbsp;') ? value : `&nbsp;${value}&nbsp;`
    p.setAttribute('data-element', `${variableElement.field_type}`)
    // Add persona field ID to the p element to be able to select it with ID
    if (variableElement.personaFieldId) p.setAttribute('data-elementid', `${variableElement.personaFieldId}`)
    parentElement.appendChild(p)
  }

  /**
   * @method _appendDropAreasOnRow
   * @description Appends the drop areas on a specified row
   * @param rowId Accepts string
   * @param variableElements Accepts VariableElement[]
   * @param rowIndex Accepts number
   * @returns void
   */
  private _appendDropAreasOnRow(variableElements: BadgeElement[], rowIndex: number, rowId: string): void {
    const row = document.getElementById(rowId) as HTMLDivElement

    if (rowId === 'row-1') {
      const topDropAreaElement = document.createElement('div')
      topDropAreaElement.classList.add(DROP_AREAS.TOP, 'hidden', 'temporary-element', 'drop-area')
      row.appendChild(topDropAreaElement)
    }

    const bottomDropAreaElement = document.createElement('div')
    bottomDropAreaElement.classList.add(DROP_AREAS.BOTTOM, 'hidden', 'temporary-element', 'drop-area')
    row.appendChild(bottomDropAreaElement)
    this._appendDropAreasOnColumnsContainers(row)
    this._appendDropAreasOnColumns(variableElements, rowIndex, row)
  }

  /**
   * @method _appendDropAreasOnColumnsContainers
   * @description Appends the drop areas on columns for a specific row
   * @param row accepts HTMLDivElement
   * @returns void
   */
  private _appendDropAreasOnColumnsContainers(row: HTMLDivElement): void {
    const columnContainers = row.querySelectorAll('.columns-container')
    // Append leftDropArea on first column only and the rest of the columns will have only rightDropArea
    columnContainers.forEach((container, key) => {
      const index = +container.id.split('-')[2] - 1
      if (index === 0) {
        const leftDropAreaElement = document.createElement('div')
        leftDropAreaElement.classList.add(DROP_AREAS.LEFT, 'hidden', 'temporary-element', 'drop-area')
        container.appendChild(leftDropAreaElement)
      }
      const rightDropAreaElement = document.createElement('div')
      rightDropAreaElement.classList.add(DROP_AREAS.RIGHT, 'hidden', 'temporary-element', 'drop-area')
      container.appendChild(rightDropAreaElement)
    })
  }

  /**
   * @method _appendDropAreasOnColumns
   * @description Appends the drop areas on columns for a specific row
   * @param variableElements accepts VariableElement[]
   * @param rowIndex accepts number
   * @param row accepts HTMLDivElement
   * @returns void
   */
  private _appendDropAreasOnColumns(variableElements: BadgeElement[], rowIndex: number, row: HTMLDivElement): void {
    // Append leftDropArea on first column only and the rest of the columns will have only rightDropArea
    variableElements.forEach((variableElement, key) => {
      const col = row.querySelector(`#col-${rowIndex + 1}-${key + 1}`) as HTMLDivElement
      const topDropAreaElement = document.createElement('div')
      if (variableElement.columnContainerIndex !== variableElements?.[key - 1]?.columnContainerIndex) {
        topDropAreaElement.classList.add(DROP_AREAS.COL_TOP, 'hidden', 'temporary-element', 'drop-area')
        col.appendChild(topDropAreaElement)
      }
      const bottomDropAreaElement = document.createElement('div')
      bottomDropAreaElement.classList.add(DROP_AREAS.COL_BOTTOM, 'hidden', 'temporary-element', 'drop-area')
      col.appendChild(bottomDropAreaElement)
    })
  }

  /**
   * @method _locateTheElement
   * @description Locate the dropped element
   * @param element Accepts VariableElement
   * @returns void
   */
  private _locateTheElement(element: BadgeElement): void {
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLDivElement
    const rows = badgeContentContainer?.children || []
    const parent = element?.targetElement?.parentElement
    let index = Object.values(rows).findIndex((row) => row?.id === parent?.id)
    let indexInRows = 1
    Object.values(rows).forEach((row, key) => {
      if (Object.values(row.children).some((col: any) => col.getAttribute('key') === element.id)) {
        indexInRows = key
        return
      }
    })

    this.isDropValid = true
    if (!this.fields.length) {
      element.columnContainerIndex = undefined
      if (
        element.field_type === BadgeElementFieldType.UPLOAD_PHOTO.toLowerCase() ||
        element.field_type === BadgeElementFieldType.QR_CODE.toLowerCase() ||
        element.field_type === BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()
      ) {
        element.style = {
          ...element.style,
          height: '20mm'
        }
      }
      this.fields.push([element])
      return
    } else if (element?.targetElement?.classList.contains(DROP_AREAS.TOP)) {
      element.columnContainerIndex = undefined
      this._handleElementHeightOverflow(element)
      this.fields.splice(index, 0, [element])
      this._removeElementField()
      return
    } else if (element?.targetElement?.classList.contains(DROP_AREAS.BOTTOM)) {
      element.columnContainerIndex = undefined
      this._handleElementHeightOverflow(element)
      this.fields.splice(index + 1, 0, [element])
      this._removeElementField()
      return
    }

    const parentCol = element?.targetElement?.parentElement?.parentElement
    if (!parentCol) return

    const rowIndex = Object.values(rows).findIndex((row) => row?.id === parentCol.id)
    const indexCol = Object.values(parentCol.children).findIndex((col) => (col as HTMLElement)?.id === element?.targetElement?.parentElement?.id)
    if (element?.targetElement?.classList.contains(DROP_AREAS.LEFT)) {
      const field = this.fields?.[rowIndex] || this.fields?.[rowIndex - 1]
      element.columnContainerIndex = undefined
      this._handleColumnHeightOverflow(field, element)
      field?.splice(indexCol, 0, element)
      this._removeElementField()
      return
    } else if (element?.targetElement?.classList.contains(DROP_AREAS.RIGHT)) {
      const field = this.fields?.[rowIndex] || this.fields?.[rowIndex - 1]
      element.columnContainerIndex = +(element?.targetElement?.parentElement?.id?.split('-')?.[2] as string) + 1
      const columnsInContainer = element?.targetElement?.parentElement?.querySelectorAll('.draggable-column') as NodeListOf<HTMLElement>
      const lastChild = columnsInContainer[columnsInContainer?.length - 1]
      const index = this.fields[rowIndex].findIndex((col) => col.id === lastChild?.getAttribute('key'))
      this._handleColumnHeightOverflow(field, element)
      field?.splice(index + 1, 0, element)
      this.fields[rowIndex].forEach((col, colIndex) => {
        if (colIndex > index) col.columnContainerIndex = (col.columnContainerIndex || 0) + 1
      })
      this._removeElementField()
      return
    } else if (element?.targetElement?.classList.contains(DROP_AREAS.COL_TOP)) {
      const container = element?.targetElement?.parentElement?.parentElement as HTMLElement
      const col = element?.targetElement?.parentElement as HTMLElement
      const colIndex = +col.id.split('-')[2] - 1
      const badgeRowIndex = +container.id.split('-')[1] - 1
      const field = this.fields?.[badgeRowIndex]
      const containerIndex = +container.id.split('-')[2] - 1
      element.columnContainerIndex = containerIndex
      this._handleColumnHeightOverflow(field, element)
      field?.splice(colIndex, 0, element)
      this._removeElementField()
      return
    } else if (element?.targetElement?.classList.contains(DROP_AREAS.COL_BOTTOM)) {
      const container = element?.targetElement?.parentElement?.parentElement as HTMLElement
      const col = element?.targetElement?.parentElement as HTMLElement
      const colIndex = +col.id.split('-')[2] - 1
      const badgeRowIndex = +container.id.split('-')[1] - 1
      const field = this.fields?.[badgeRowIndex]
      const containerIndex = +container.id.split('-')[2] - 1
      element.columnContainerIndex = containerIndex
      this._handleColumnHeightOverflow(field, element)
      field?.splice(colIndex + 1, 0, element)
      this._removeElementField()
      return
    }
  }

  /**
   * @description Handle Column height overflow
   * @param field accepts BadgeElement[]
   * @param element accepts BadgeElement
   * @returns void
   */
  private _handleColumnHeightOverflow(field: BadgeElement[], element: BadgeElement): void {
    let height = field
      .filter((col) => col.columnContainerIndex === element.columnContainerIndex)
      .map((col) => parseFloat(col.style?.height as string))
      .reduce((sum, currentValue) => sum + this._BadgeService.convertMMToPX(currentValue) + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM), 0)
    let columnHeightMM = parseFloat(element.style?.height as string)
    const isExist = field.some((col) => col.columnContainerIndex === element.columnContainerIndex && col.id === element.previous)
    if (!isExist) height += this._BadgeService.convertMMToPX(columnHeightMM)
    const rowRect = document.getElementById(`row-${this.fields.indexOf(field) + 1}`)?.getBoundingClientRect() as DOMRect
    const mmPerStep = 2
    while (Math.round(rowRect.height) < Math.round(height) && columnHeightMM >= DEFAULT_HEIGHT_IN_MM) {
      columnHeightMM -= mmPerStep
      height -= this._BadgeService.convertMMToPX(mmPerStep)
    }
    element.style = {
      ...element.style,
      height: `${columnHeightMM}mm`
    }
  }

  /**
   * @method _removeElementField
   * @description remove field to append it in another place
   * @returns void
   */
  private _removeElementField() {
    if (!this._draggedVariableElement) return
    for (let [rowIndex, field] of Object.entries(this.fields)) {
      const columnIndex = field.findIndex((fieldCol) => this._draggedVariableElement?.id === fieldCol.id)
      if (columnIndex !== -1) {
        this.fields[+rowIndex].splice(columnIndex, 1)
        if (!this.fields[+rowIndex].length) this.fields.splice(+rowIndex, 1)
        break
      }
    }
    this._draggedVariableElement = undefined
  }

  /**
   * @method _appendColumnDragArea
   * @description append drag area in column
   * @param variableElement accepts VariableElement
   * @param col accepts HTMLElement
   * @returns void
   */
  private _appendColumnDragArea(col: HTMLElement): void {
    const dragArea = document.createElement('div')
    dragArea.classList.add('drag-area', 'temporary-element')
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLElement
    dragArea.style.width = badgeContentContainer.style.paddingLeft
    dragArea.style.left = `-${badgeContentContainer.style.paddingLeft}`
    dragArea.innerHTML = `
      <svg style="width: 75%; height: 100%" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.64552 3.70906C3.64601 3.70906 4.45706 2.91658 4.45706 1.939C4.45706 0.961428 3.64601 0.168945 2.64552 0.168945C1.64504 0.168945 0.833984 0.961428 0.833984 1.939C0.833984 2.91658 1.64504 3.70906 2.64552 3.70906Z" fill="white"/>
        <path d="M2.64552 9.77083C3.64601 9.77083 4.45706 8.97835 4.45706 8.00077C4.45706 7.0232 3.64601 6.23071 2.64552 6.23071C1.64504 6.23071 0.833984 7.0232 0.833984 8.00077C0.833984 8.97835 1.64504 9.77083 2.64552 9.77083Z" fill="white"/>
        <path d="M2.64552 15.8326C3.64601 15.8326 4.45706 15.0401 4.45706 14.0625C4.45706 13.085 3.64601 12.2925 2.64552 12.2925C1.64504 12.2925 0.833984 13.085 0.833984 14.0625C0.833984 15.0401 1.64504 15.8326 2.64552 15.8326Z" fill="white"/>
        <path d="M9.34531 3.70906C10.3458 3.70906 11.1568 2.91658 11.1568 1.939C11.1568 0.961428 10.3458 0.168945 9.34531 0.168945C8.34482 0.168945 7.53377 0.961428 7.53377 1.939C7.53377 2.91658 8.34482 3.70906 9.34531 3.70906Z" fill="white"/>
        <path d="M9.34531 9.77083C10.3458 9.77083 11.1568 8.97835 11.1568 8.00077C11.1568 7.0232 10.3458 6.23071 9.34531 6.23071C8.34482 6.23071 7.53377 7.0232 7.53377 8.00077C7.53377 8.97835 8.34482 9.77083 9.34531 9.77083Z" fill="white"/>
        <path d="M9.34531 15.8326C10.3458 15.8326 11.1568 15.0401 11.1568 14.0625C11.1568 13.085 10.3458 12.2925 9.34531 12.2925C8.34482 12.2925 7.53377 13.085 7.53377 14.0625C7.53377 15.0401 8.34482 15.8326 9.34531 15.8326Z" fill="white"/>
      </svg>
    `
    col.appendChild(dragArea)
    this._handleDragDropEvents(col)
  }

  /**
   * @method _updateBadgeForm
   * @description update the badge form
   * @returns void
   */
  private _updateBadgeForm(): void {
    const badge = document.getElementById('badge') as HTMLDivElement
    const badgeText = this._BadgeService.clearBadgeExtraHTML(badge.outerHTML, this.getFormControl('background_preprinted').value)
    this.badgeForm.get(`badge_${this.currentSide}_html_content`)?.setValue(badgeText)
  }

  /**
   * @method getFormControl
   * @description Get control from badgeForm
   * @returns FormControl
   */
  public getFormControl(controlName: string): FormControl {
    return this.badgeForm.get(controlName) as FormControl
  }

  /**
   * @method _handleDroppingIfBadgeHasSpaceToDrop
   * @description Checks if the badge has space to drop more elements
   * @returns void
   */
  private _handleDroppingIfBadgeHasSpaceToDrop(variableElement: BadgeElement): void {
    const rowIndex = this.fields.findIndex((row) => row.some((col) => col.id === variableElement.id))
    const badge = document.getElementById('badge') as HTMLDivElement
    if (!variableElement || !badge) return
    const badgeContentContainer = badge.querySelector('#badge-content-container') as HTMLDivElement
    if (!badgeContentContainer) return
    this._resetDropZonesValidation()

    // Get QR Code size as default size
    const defaultHeight = this._BadgeService.convertMMToPX(DEFAULT_HEIGHT_IN_MM) || 0
    const hasTopAndDownSpace = Math.round(this._BadgeService.badgeHeightInPX - this._BadgeService.getElementHeight('#badge-content-container')) > Math.round(defaultHeight)
    // Hide drop Areas if there is no enough space
    if ((!hasTopAndDownSpace && rowIndex === -1) || (!hasTopAndDownSpace && rowIndex !== -1 && this.fields[rowIndex]?.length > 1)) {
      const topDropArea = badgeContentContainer.querySelector(`.${DROP_AREAS.TOP}`) as HTMLDivElement
      topDropArea.classList.add('hide')
      const bottomDropAreas = badgeContentContainer.querySelectorAll(`.${DROP_AREAS.BOTTOM}`)
      Object.values(bottomDropAreas).forEach((bottomDropArea) => bottomDropArea.classList.add('hide'))
    }

    let invalidContainersCount = 0
    const columnsContainers = document.querySelectorAll('.columns-container')
    columnsContainers.forEach((container) => {
      const columns = container.querySelectorAll('.draggable-column')
      const isColumnExist = Object.values(columns).some((col) => col.getAttribute('key') === variableElement.id)
      const containerHasTopAndDownSpace = this._BadgeService.containerHasEnoughSpace(container as HTMLElement, defaultHeight)
      if ((!containerHasTopAndDownSpace && !isColumnExist) || (isColumnExist && columns?.length === 1)) {
        invalidContainersCount++
        const topDropArea = container.querySelector(`.${DROP_AREAS.COL_TOP}`) as HTMLDivElement
        topDropArea?.classList.add('hide')
        const bottomDropAreas = container.querySelectorAll(`.${DROP_AREAS.COL_BOTTOM}`)
        Object.values(bottomDropAreas).forEach((bottomDropArea) => bottomDropArea.classList.add('hide'))
      }
    })

    // IMPORTANT: keep the same behavior as before for left/right drop-area visibility.
    // Previously, `columnsLimit` was effectively `floor(templateWidthMM / minColumnWidthMM)`.
    // We only scale it by the *current content width percentage* so narrowing reduces capacity,
    // but at 100% width the behavior is identical to the pre-width-feature implementation.
    const contentWidthPx = this._getBadgeContentContainerWidthPx()
    const badgeWidthPx = badge.getBoundingClientRect().width || contentWidthPx || 1
    const contentWidthMM = ((contentWidthPx || badgeWidthPx) / badgeWidthPx) * this.selectedTemplate!.width
    const columnsLimit = Math.max(1, Math.floor(contentWidthMM / this._minColumnWidthInMM))
    const rows = badgeContentContainer.children
    let rightAndLeftSpaceCount = 0
    Object.values(rows).forEach((row, key) => {
      //Hide drop areas if there is not enough space
      const columnsContainers = row.querySelectorAll('.columns-container')
      const columnsCount = this.fields[rowIndex]?.filter((col) => col.columnContainerIndex === variableElement.columnContainerIndex)?.length
      const topAndRightDropArea = row.querySelectorAll(`.${DROP_AREAS.COL_TOP} , .${DROP_AREAS.COL_BOTTOM}`)
      if ((columnsLimit <= columnsContainers.length && key !== rowIndex) || (columnsCount > 1 && columnsLimit <= columnsContainers.length && key === rowIndex)) {
        rightAndLeftSpaceCount++
        const leftDropArea = row.querySelector(`.${DROP_AREAS.LEFT}`) as HTMLDivElement
        leftDropArea.classList.add('hide')
        const rightDropAreas = row.querySelectorAll(`.${DROP_AREAS.RIGHT}`)
        Object.values(rightDropAreas).forEach((rightDropArea) => rightDropArea.classList.add('hide'))
        topAndRightDropArea.forEach((dropArea) => dropArea.classList.add('col-w-full'))
      } else topAndRightDropArea.forEach((dropArea) => dropArea.classList.remove('col-w-full'))
    })
    if (badge && this.isDragging && rightAndLeftSpaceCount === this.fields.length && !hasTopAndDownSpace && invalidContainersCount === columnsContainers.length)
      badge.classList.add('invalid-dropping')
    this._handleExtraDropAreas()
  }

  /**
   * @method _resetDropZonesValidation
   * @description Reset Drop zones validation
   * @returns void
   */
  private _resetDropZonesValidation() {
    const badge = document.getElementById('badge') as HTMLDivElement
    const dropAreas = badge.querySelectorAll(`.${this._dropAreasList.join(', .')}`)
    if (!dropAreas) return
    dropAreas.forEach((dropArea) => dropArea.classList.remove('hide'))
  }

  /**
   * @method _handleDragDropEvents
   * @description Handle drag & drop events when drag from badge
   * @param col accepts HTMLElement
   * @returns void
   */
  private _handleDragDropEvents(col: HTMLElement): void {
    const dragRef = this._DragDrop.createDrag(col)
    let placeholder: HTMLElement
    // Event when the drag starts
    const startDraggingEventSubscription = dragRef.started.pipe(takeUntil(this._columnsSubscriptions$)).subscribe({
      next: (event) => {
        // Create and insert the placeholder
        placeholder = col.cloneNode(true) as HTMLElement
        placeholder.classList.add('col-placeholder')
        placeholder.style.width = `${col.offsetWidth}px`
        placeholder.style.height = `${col.offsetHeight}px`
        placeholder.style.left = `${col.offsetLeft}px`
        placeholder.style.top = `${col.offsetTop}px`
        event.source.getRootElement()?.parentElement?.insertBefore(placeholder, event.source.getRootElement().nextSibling)
        const containerColumnsCount = event.source.getRootElement().parentElement?.querySelectorAll('.draggable-column:not(.col-placeholder)').length
        if (containerColumnsCount === 1) {
          event.source.getRootElement().parentElement?.classList?.add('hide-left-right-drop-area')
          event.source.getRootElement().parentElement?.previousElementSibling?.classList?.add('hide-right-drop-area')
          event.source.getRootElement().parentElement?.parentElement?.classList?.add('hide-column-drop-areas')
        }
        const parentRow = event.source.getRootElement().parentElement?.parentElement
        if (parentRow && parentRow?.querySelectorAll('.draggable-column:not(.col-placeholder)').length === 1) {
          parentRow.classList?.add('hide-top-drop-area', 'hide-bottom-drop-area')
          const prevRow = parentRow.previousElementSibling
          if (prevRow) prevRow.classList.add('hide-bottom-drop-area')
        }
        event.source.getRootElement().classList.add('hide-column-top-bottom-drop-area')

        const prevCol = event.source.getRootElement().previousElementSibling
        if (prevCol) prevCol.classList.add('hide-right-drop-area')
        this.fields.forEach((field) => {
          field.forEach((fieldCol) => {
            if (fieldCol.id === event.source.getRootElement().getAttribute('key')) {
              this._draggedVariableElement = fieldCol
              this._BadgeService.isDraggingStarted$.next({ ...this._draggedVariableElement })
            }
          })
        })
        document.body.style.cursor = 'grabbing'
      }
    })

    // Event happens while dragging
    const movedEventSubscription = dragRef.moved.pipe(takeUntil(this._columnsSubscriptions$)).subscribe((event) => {
      const target = document.elementFromPoint(event.pointerPosition.x, event.pointerPosition.y) as HTMLElement
      if (!target) return
      document.querySelectorAll('.temporary-element.drop-area').forEach((dropArea) => dropArea?.classList.remove('active-drop-area'))
      const targetElement = target.classList.contains('spanned-drop-area') ? (target?.parentElement as HTMLElement) : target
      if (targetElement?.classList.contains('drop-area')) targetElement.classList.add('active-drop-area')
      const isInsideBadge = !!target.closest('#badge')
      this._BadgeService.isDragging$.next(isInsideBadge)
    })

    // Event happens on dropping the element
    dragRef.ended.pipe(takeUntil(this._columnsSubscriptions$)).subscribe((event) => {
      this.isDragging = false
      document.body.style.cursor = 'default'
      this._BadgeService.isDragging$.next(false)

      // Get the target element from the event
      const targetElement = event.dropPoint ? (document.elementFromPoint(event.dropPoint.x, event.dropPoint.y) as HTMLElement) : null

      if (!targetElement) {
        placeholder.remove()
        this._draggedVariableElement = undefined
        ;(document.querySelector('#badge') as HTMLDivElement).classList.remove('invalid-dropping')
        return
      }

      const finalTargetElement = targetElement.classList.contains('spanned-drop-area') ? (targetElement.parentElement as HTMLElement) : targetElement

      event.source.getRootElement().style.transform = ''
      dragRef.reset()
      event.source.getRootElement().parentElement?.classList?.remove('hide-left-right-drop-area')
      event.source.getRootElement().parentElement?.previousElementSibling?.classList?.remove('hide-right-drop-area')
      event.source.getRootElement().parentElement?.parentElement?.classList?.remove('hide-column-drop-areas')
      const parentRow = event.source.getRootElement().parentElement?.parentElement
      if (parentRow && parentRow?.querySelectorAll('.draggable-column:not(.col-placeholder)').length === 1) {
        parentRow.classList?.remove('hide-top-drop-area', 'hide-bottom-drop-area')
        const prevRow = parentRow.previousElementSibling
        if (prevRow) prevRow.classList.remove('hide-bottom-drop-area')
      }
      event.source.getRootElement().classList.remove('hide-column-top-bottom-drop-area')

      const prevCol = event.source.getRootElement().previousElementSibling
      if (prevCol) {
        prevCol.classList.remove('hide-right-drop-area')
      }

      if (!Object.values(finalTargetElement.classList).some((className: string) => this._dropAreasList.includes(className))) {
        placeholder.remove()
        this._draggedVariableElement = undefined
        ;(document.querySelector('#badge') as HTMLDivElement).classList.remove('invalid-dropping')
        return
      }

      startDraggingEventSubscription.unsubscribe()
      movedEventSubscription.unsubscribe()
      placeholder.remove()
      const variableElement = { ...this._draggedVariableElement } as BadgeElement
      variableElement.targetElement = finalTargetElement
      variableElement.previous = variableElement.id
      variableElement.id = crypto.randomUUID()
      this._BadgeService.hasDropped$.next(variableElement)
    })
  }

  /**
   * @method _calculateBadgeMargin
   * @description Calculate badge content container margin
   * @returns string
   */
  private _calculateBadgeMargin(variableElement: BadgeElement, badgeContentContainer: HTMLDivElement): string {
    const defaultHeight = this._calculateDefaultHeightInPX(variableElement) || 0
    const marginBottom = window.getComputedStyle(badgeContentContainer).marginBottom
    const margin =
      parseInt(marginBottom) > 0 || this.pushedSpace > 0
        ? parseInt(marginBottom) - defaultHeight - this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)
        : parseInt(marginBottom) + defaultHeight + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)

    const hasTopAndDownSpace = this._BadgeService.badgeHeightInPX - this._BadgeService.getElementHeight('#badge-content-container') > defaultHeight
    const percentage = (margin / badgeContentContainer.clientWidth) * 100
    return hasTopAndDownSpace &&
      Math.abs(parseInt(marginBottom)) + this._BadgeService.getElementHeight('#badge-content-container') + defaultHeight > this._BadgeService.badgeHeightInPX
      ? `${percentage}%`
      : badgeContentContainer?.style?.marginBottom || '0%'
  }

  /**
   * @method _handleExtraDropAreas
   * @description Handle extra drop areas on columns and rows
   * @returns void
   */
  private _handleExtraDropAreas(): void {
    const badge = document.getElementById('badge') as HTMLDivElement
    const badgeContentContainer = badge.querySelector('#badge-content-container') as HTMLDivElement
    const badgeRect = badge.getBoundingClientRect()
    if (!badgeContentContainer) return
    const badgeContentContainerRect = badgeContentContainer.getBoundingClientRect()
    this.fields.forEach((field, rowIndex) => {
      const row = document.getElementById(`row-${rowIndex + 1}`) as HTMLDivElement
      const rowRect = row.getBoundingClientRect()
      // Create extra top drop area and calculate its 'height' and 'top'
      const topDropAreaElement = row.querySelector(`.${DROP_AREAS.TOP}`) as HTMLDivElement
      if (topDropAreaElement) {
        const topSpace = badgeContentContainerRect.top - badgeRect.top
        const height = `${rowRect.height / 2 + topSpace}px`
        const width = `calc(100% + ${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE)}px)`
        const left = `calc(${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE) / -2}px)`
        const top = `-${topSpace - this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
        topDropAreaElement.innerHTML = ''
        topDropAreaElement.appendChild(this._createExtraDropAreaElement({ height, top, left, width }))
      }

      // Create extra top drop area and calculate its 'height' and 'top'
      const colTopDropAreaElement = row.querySelectorAll('.col-top-drop-area')
      colTopDropAreaElement.forEach((dropArea) => {
        const colRect = (dropArea.parentElement as HTMLElement).getBoundingClientRect()
        const colContainerRect = (dropArea.parentElement?.parentElement as HTMLElement).getBoundingClientRect()
        const topSpace = colRect.top - colContainerRect.top
        const height = `${colRect.height / 2 + (topSpace + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)) / 2}px`
        const top = `${(topSpace - this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)) / -2}px`
        dropArea.innerHTML = ''
        dropArea.appendChild(this._createExtraDropAreaElement({ height, top }))
      })

      // Create extra top drop area and calculate its 'height' and 'top'
      const colBottomDropAreaElement = row.querySelectorAll(`.${DROP_AREAS.COL_BOTTOM}`)
      colBottomDropAreaElement.forEach((dropArea) => {
        const colRect = (dropArea.parentElement as HTMLElement).getBoundingClientRect()
        const colContainerRect = (dropArea.parentElement?.parentElement as HTMLElement).getBoundingClientRect()
        const bottomSpace = colContainerRect.bottom - colRect.bottom
        const columnIndex = dropArea.parentElement?.id.split('-')[2]
        const nextSiblingColumnRect = dropArea.parentElement?.nextElementSibling?.getBoundingClientRect() as DOMRect
        const height =
          dropArea.parentElement?.nextElementSibling?.id === `col-${rowIndex + 1}-${columnIndex}`
            ? `${colRect.height / 2 + nextSiblingColumnRect.height / 2 + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
            : `${colRect.height / 2 + (bottomSpace + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)) / 2}px`
        const top = `${-(colRect.height / 2)}px`
        dropArea.innerHTML = ''
        dropArea.appendChild(this._createExtraDropAreaElement({ height, top }))
      })

      // Create extra bottom drop area and calculate its 'height' and 'top'
      const bottomDropAreaElement = row.querySelector(`.${DROP_AREAS.BOTTOM}`) as HTMLDivElement
      if (bottomDropAreaElement) {
        const bottomSpace = badgeRect.bottom - badgeContentContainerRect.bottom
        const height = `${
          row?.nextElementSibling?.id === `row-${rowIndex + 2}`
            ? rowRect.height / 2 + row.nextElementSibling?.clientHeight / 2 + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)
            : rowRect.height / 2 + bottomSpace
        }px`
        const width = `calc(100% + ${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE)}px)`
        const left = `calc(${this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE) / -2}px)`
        const top = `-${rowRect.height / 2}px`
        bottomDropAreaElement.innerHTML = ''
        bottomDropAreaElement.appendChild(this._createExtraDropAreaElement({ height, width, left, top }))
      }

      const columnsContainers = row.querySelectorAll('.columns-container')
      Object.values(columnsContainers).forEach((container, key) => {
        // Create extra left drop area and calculate its 'width' and 'left'
        const leftDropAreaElement = container.querySelector(`.${DROP_AREAS.LEFT}`) as HTMLDivElement
        if (leftDropAreaElement) {
          const width = `${container.clientWidth / 4 + this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE / 2) + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          const left = `-${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM * 2)}px`
          leftDropAreaElement.innerHTML = ''
          leftDropAreaElement.appendChild(this._createExtraDropAreaElement({ width, left }))
        }

        // Create extra right drop area and calculate its 'width' and 'left'
        const rightDropAreaElement = container.querySelector(`.${DROP_AREAS.RIGHT}`) as HTMLDivElement
        if (rightDropAreaElement) {
          const dropAreaWidth =
            container?.nextElementSibling?.id === `col-${rowIndex + 1}-${key + 2}`
              ? container.clientWidth / 4 + container.nextElementSibling?.clientWidth / 4 + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)
              : container.clientWidth / 4 + this._BadgeService.convertMMToPX(BADGE_LEFT_AND_RIGHT_SPACE / 2) + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)
          const dropAreaLeft =
            container?.nextElementSibling?.id === `col-${rowIndex + 1}-${key + 2}`
              ? container.clientWidth / 4
              : container.clientWidth / 4 + this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)
          rightDropAreaElement.innerHTML = ''
          rightDropAreaElement.appendChild(this._createExtraDropAreaElement({ width: `${dropAreaWidth}px`, left: `-${dropAreaLeft}px` }))
        }
      })
    })
  }

  /**
   * @method _createExtraDropAreaElement
   * @description Create html span element as an extra drop area
   * @param style accepts {width?: string, height?: string, left?: string, top?: string}
   * @returns HTMLSpanElement
   */
  private _createExtraDropAreaElement(style: { width?: string; height?: string; left?: string; top?: string }): HTMLSpanElement {
    const span = document.createElement('span') as HTMLSpanElement
    span.classList.add('spanned-drop-area')
    span.style.width = style?.width || span.style?.width
    span.style.height = style?.height || span.style?.height
    span.style.left = style?.left || span.style?.left
    span.style.top = style?.top || span.style?.top
    return span
  }

  /**
   * @method _appendTooltipSettings
   * @description Append tooltip settings component inside a column
   * @param col accepts HTMLElement
   * @returns void
   */
  private _appendTooltipSettings(col: HTMLElement, variableElement?: BadgeElement): void {
    const columnIndex = +col.id.split('-')[2] - 1
    const rowIndex = +col.id.split('-')[1] - 1
    if (!variableElement) variableElement = this.fields[rowIndex][columnIndex]
    // Create new badge-element-tooltip-settings component
    const factory = this._ComponentFactoryResolver.resolveComponentFactory(BadgeElementTooltipSettingsComponent) // Resolve the component
    const BadgeElementTooltipSettingsComponentRef = factory.create(this._Injector)
    BadgeElementTooltipSettingsComponentRef.instance.columnElement = col
    BadgeElementTooltipSettingsComponentRef.instance.variableElements = this.fields
    BadgeElementTooltipSettingsComponentRef.instance.selectedTemplate = this.selectedTemplate
    const isDtcmQrField = variableElement?.field_type === BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()
    // Only Micetribe QR supports the eye toggle; DTCM QR never shows it.
    // Keep DTCM QR "visible" state stable (toggle not available anyway).
    BadgeElementTooltipSettingsComponentRef.instance.isQrNumberVisible = isDtcmQrField || variableElement?.style?.['qr-number-display'] !== 'none'
    BadgeElementTooltipSettingsComponentRef.instance.variableElementStyleChanged.pipe(takeUntil(this._columnsSubscriptions$)).subscribe({
      next: (style) => {
        if (variableElement) {
          variableElement.style = style
          this._updateBadgeForm()
        }
      }
    })
    BadgeElementTooltipSettingsComponentRef.instance.qrNumberVisibilityChanged.pipe(takeUntil(this._columnsSubscriptions$)).subscribe({
      next: (isVisible) => {
        const qrField = col.querySelector(
          `[data-element="${BadgeElementFieldType.QR_CODE.toLowerCase()}"], [data-element="${BadgeElementFieldType.DTCM_QR_CODE.toLowerCase()}"]`
        ) as HTMLElement
        const qrNumberContainer = qrField?.querySelector('.qr-number')?.parentElement as HTMLElement
        const qrImage = qrField?.querySelector('img') as HTMLImageElement
        if (!variableElement || !qrNumberContainer || !qrImage) return

        qrNumberContainer.style.display = isVisible ? 'flex' : 'none'
        // Keep QR image dimensions stable when toggling QR number text visibility.
        qrImage.style.height = '78%'
        qrField.style.justifyContent = isVisible ? 'center' : 'flex-start'
        if (qrField.getAttribute('data-element') === BadgeElementFieldType.QR_CODE.toLowerCase()) qrField.style.aspectRatio = '1 / 1.2'
        variableElement.style = {
          ...variableElement.style,
          'qr-number-display': qrNumberContainer.style.display
        }
        BadgeElementTooltipSettingsComponentRef.instance.isQrNumberVisible = isVisible
        if (isVisible) this._BadgeService.resizeTextValueToFitParent(rowIndex)
        this._updateBadgeForm()
      }
    })
    BadgeElementTooltipSettingsComponentRef.instance.variableElementsChanges$
      .pipe(
        takeUntil(this._columnsSubscriptions$),
        finalize(() => {
          this._ApplicationRef.detachView(BadgeElementTooltipSettingsComponentRef.hostView)
          BadgeElementTooltipSettingsComponentRef.destroy()
        })
      )
      .subscribe({
        next: (variableElements) => {
          this.fields = variableElements
          this._handleBadgeContainerSize()
          this._resetDropZonesValidation()
          this._createBadgeContent()
          this._updateBadgeForm()
        }
      })
    BadgeElementTooltipSettingsComponentRef.location.nativeElement.classList.add('temporary-element')
    this._ApplicationRef.attachView(BadgeElementTooltipSettingsComponentRef.hostView)
    col.appendChild(BadgeElementTooltipSettingsComponentRef.location.nativeElement)
  }

  /**
   * @method _calculateDefaultHeight
   * @description Calculate the default height of an element, and make sure that height is more than or equal 4 (4mm is the minimum height)
   * @param variableElement accepts BadgeElement
   * @returns number
   */
  private _calculateDefaultHeightInPX(variableElement: BadgeElement = this._draggedVariableElement as BadgeElement): number | void {
    if (!variableElement?.style?.height) return
    if (parseFloat(variableElement.style.height) < 4) variableElement.style.height = '4mm'
    return (parseFloat(variableElement.style.height) * this._BadgeService.badgeHeightInPX) / this.selectedTemplate!.height
  }

  /**
   * @method _handleColumnHeightElement
   * @description Reflect the variable element height to the HTML
   * @param col accepts HTMLElement
   * @returns void
   */
  private _handleColumnHeightElement(col: HTMLElement) {
    const heightElement = col.querySelector('.height-element') as HTMLElement
    const rowIndex = +col.id.split('-')[1] - 1
    const columnIndex = +col.id.split('-')[2] - 1
    const heightInMM = this.fields[rowIndex][columnIndex].style?.height as string
    const badgeContentContainer = document.getElementById('badge-content-container') as HTMLElement
    // Reflect the variable element height to the heightElement if exist, otherwise create a new heightElement
    if (heightElement) {
      heightElement.style.width = badgeContentContainer.style.paddingRight
      heightElement.style.right = `-${badgeContentContainer.style.paddingRight}`
      const span = heightElement.querySelector('span') as HTMLParagraphElement
      span.innerText = parseInt(heightInMM).toString()
      return
    }
    const div = document.createElement('div')
    div.classList.add('height-element', 'temporary-element')
    div.style.width = badgeContentContainer.style.paddingRight
    div.style.right = `-${badgeContentContainer.style.paddingRight}`
    const span = document.createElement('span')
    span.innerText = parseInt(heightInMM).toString()
    div.appendChild(span)
    col.appendChild(div)
  }

  /**
   * @method _handleBadgeContainerSize
   * @description Prevent badgeContainer from exceeding tha parent component width or height
   * @returns void
   */
  private _handleBadgeContainerSize(): void {
    const parentRatio = this.badgeDesignContainer.nativeElement.clientWidth / this.badgeDesignContainer.nativeElement.clientHeight
    if (parseFloat(parentRatio.toFixed(1)) <= parseFloat(this.aspectRatio.toFixed(1))) {
      this.badgeContainer.nativeElement.style.width = '100%'
      this.badgeContainer.nativeElement.style.height = 'auto'
    } else {
      this.badgeContainer.nativeElement.style.width = 'auto'
      this.badgeContainer.nativeElement.style.height = '100%'
    }
  }

  /**
   * @method _handleDropAreaSize
   * @description Handle the position and size of drop areas
   * @returns void
   */
  private _handleDropAreaSize(): void {
    const dropAreas = document.querySelectorAll(`.${this._dropAreasList.join(', .')}`)
    dropAreas.forEach((dropArea) => {
      switch (dropArea.classList[0].replace('col-', '')) {
        case DROP_AREAS.TOP: {
          ;(dropArea as HTMLElement).style.top = `-${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          ;(dropArea as HTMLElement).style.height = `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          break
        }
        case DROP_AREAS.BOTTOM: {
          ;(dropArea as HTMLElement).style.bottom = `-${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          ;(dropArea as HTMLElement).style.height = `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          break
        }
        case DROP_AREAS.LEFT: {
          ;(dropArea as HTMLElement).style.left = `-${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          ;(dropArea as HTMLElement).style.width = `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          break
        }
        case DROP_AREAS.RIGHT: {
          ;(dropArea as HTMLElement).style.right = `-${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          ;(dropArea as HTMLElement).style.width = `${this._BadgeService.convertMMToPX(DROP_AREA_SIZE_IN_MM)}px`
          break
        }
      }
    })
  }

  /**
   * @method _resetOrder
   * @description reset columns orders
   * @returns void
   */
  private _resetOrder(): void {
    this.fields.forEach((field, rowIndex) => {
      let columns = JSON.parse(JSON.stringify(field))
      field.forEach((col, colIndex) => {
        if (colIndex === 0) field[colIndex].columnContainerIndex = 0
        else {
          if (columns[colIndex].columnContainerIndex === columns[colIndex - 1].columnContainerIndex) field[colIndex].columnContainerIndex = field[colIndex - 1].columnContainerIndex
          else field[colIndex].columnContainerIndex = (field[colIndex - 1].columnContainerIndex as number) + 1
        }
      })
      const columnsCountInRow = this._BadgeService.getColumnsCountInRow(rowIndex, this.fields)
      if (columnsCountInRow === 1) {
        const elements = this.fields[rowIndex]
        this.fields.splice(rowIndex, 1)
        let startRowIndex = rowIndex
        let columnsCount = elements.length
        while (columnsCount > 0) {
          const element = elements.shift() as BadgeElement
          element.columnContainerIndex = 0
          this.fields.splice(startRowIndex, 0, [element])
          startRowIndex++
          columnsCount--
        }
      }
    })
  }

  /**
   * @description Reduce element height to fit the badge if there is overflow
   * @param element accepts BadgeElement
   * @returns void
   */
  private _handleElementHeightOverflow(element: BadgeElement): void {
    const isRowWithOneColumn = this.fields.some((field, index) => field.some((col) => col.id === element.previous && field.length === 1))
    if (isRowWithOneColumn) return

    // Reduce the element height to fit the available space
    this._customizedHeightInMM = parseFloat(element.style?.height as string)
    while (
      Math.round(this._BadgeService.convertMMToPX(this._customizedHeightInMM + 2)) >
      Math.round(this._BadgeService.badgeHeightInPX - this._BadgeService.getElementHeight('#badge-content-container'))
    ) {
      this._customizedHeightInMM -= 2
    }
    element.style = {
      ...element.style,
      height: `${this._customizedHeightInMM}mm`
    }
  }
}
